/**
 * Global Validation State Manager
 * 
 * Manages validation state and EventSource connections globally, independent of component lifecycle.
 * This allows validations to continue running even when components unmount (navigation, sorting, etc.).
 */

import { fetchAuthSession } from 'aws-amplify/auth';

class GlobalValidationManager {
    constructor() {
        // Store active validations with their EventSource connections
        this.activeValidations = new Map();

        // Store validation results for all stores
        this.validationResults = new Map();

        // Store validation progress for active validations
        this.validationProgress = new Map();

        // Store validation errors
        this.validationErrors = new Map();

        // Listeners for state changes
        this.listeners = new Set();
    }

    /**
     * Start validation for a store
     * @param {Object} store - Store object with name, pages, isTestStore, etc.
     * @returns {Promise} - Resolves when validation completes
     */
    async startValidation(store) {
        const storeKey = store.name;

        // If already validating this store, return existing promise
        if (this.activeValidations.has(storeKey)) {
            console.log(`Validation already active for ${storeKey}`);
            return this.activeValidations.get(storeKey).promise;
        }

        // Clear any previous error
        this.validationErrors.delete(storeKey);

        // Initialize progress
        this.validationProgress.set(storeKey, {
            currentPage: 0,
            totalPages: 0,
            currentPageName: 'Initializing...',
            isActive: true
        });

        this.notifyListeners();

        const validationPromise = new Promise(async (resolve, reject) => {
            try {
                const session = await fetchAuthSession();
                const token = session.tokens?.idToken?.toString();

                if (!token) {
                    throw new Error('No authentication token available');
                }

                const baseUrl = store.isTestStore
                    ? `/api/stores/test-stores/${encodeURIComponent(store.testStoreName)}/validate-progress`
                    : `/api/stores/${encodeURIComponent(store.name)}/validate-progress`;

                const validationUrl = `${baseUrl}?token=${encodeURIComponent(token)}`;
                const eventSource = new EventSource(validationUrl);

                // Store the EventSource for potential cleanup
                this.activeValidations.set(storeKey, {
                    eventSource,
                    promise: validationPromise,
                    store
                });

                eventSource.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);

                        if (data.type === 'progress') {
                            this.validationProgress.set(storeKey, {
                                currentPage: data.currentPage || 0,
                                totalPages: data.totalPages || 0,
                                currentPageName: data.currentPageName || data.message || 'Processing...',
                                isActive: true
                            });
                            this.notifyListeners();
                        } else if (data.type === 'complete') {
                            // Update progress to completed
                            this.validationProgress.set(storeKey, {
                                currentPage: data.totalPages || 0,
                                totalPages: data.totalPages || 0,
                                currentPageName: 'Completed',
                                isActive: false
                            });

                            // Store validation results
                            if (data.validationResults) {
                                this.validationResults.set(storeKey, data.validationResults);
                            }

                            this.notifyListeners();

                            // Cleanup
                            eventSource.close();
                            this.activeValidations.delete(storeKey);

                            // Clear progress after delay
                            setTimeout(() => {
                                this.validationProgress.delete(storeKey);
                                this.notifyListeners();
                            }, 2000);

                            resolve(data.validationResults);
                        } else if (data.type === 'error') {
                            const errorMessage = data.error || data.message || 'Validation failed';
                            this.validationErrors.set(storeKey, errorMessage);

                            this.validationProgress.set(storeKey, {
                                currentPage: 0,
                                totalPages: 0,
                                currentPageName: '',
                                isActive: false
                            });

                            this.notifyListeners();

                            // Cleanup
                            eventSource.close();
                            this.activeValidations.delete(storeKey);

                            // Clear progress after delay
                            setTimeout(() => {
                                this.validationProgress.delete(storeKey);
                                this.notifyListeners();
                            }, 2000);

                            reject(new Error(errorMessage));
                        }
                    } catch (parseError) {
                        console.error('Error parsing progress data:', parseError);
                        eventSource.close();
                        this.activeValidations.delete(storeKey);
                        reject(parseError);
                    }
                };

                eventSource.onerror = (error) => {
                    console.error('EventSource error:', error);
                    const errorMessage = 'Connection error during validation';
                    this.validationErrors.set(storeKey, errorMessage);

                    this.validationProgress.set(storeKey, {
                        currentPage: 0,
                        totalPages: 0,
                        currentPageName: '',
                        isActive: false
                    });

                    this.notifyListeners();

                    // Cleanup
                    eventSource.close();
                    this.activeValidations.delete(storeKey);

                    // Clear progress after delay
                    setTimeout(() => {
                        this.validationProgress.delete(storeKey);
                        this.notifyListeners();
                    }, 2000);

                    reject(new Error(errorMessage));
                };

            } catch (error) {
                console.error('Validation setup error:', error);
                this.validationErrors.set(storeKey, error.message);

                this.validationProgress.set(storeKey, {
                    currentPage: 0,
                    totalPages: 0,
                    currentPageName: '',
                    isActive: false
                });

                this.notifyListeners();
                this.activeValidations.delete(storeKey);

                // Clear progress after delay
                setTimeout(() => {
                    this.validationProgress.delete(storeKey);
                    this.notifyListeners();
                }, 2000);

                reject(error);
            }
        });

        // Store the promise reference
        if (this.activeValidations.has(storeKey)) {
            this.activeValidations.get(storeKey).promise = validationPromise;
        }

        return validationPromise;
    }

    /**
     * Get validation state for a specific store
     * @param {string} storeName - Name of the store
     * @returns {Object} - Validation state
     */
    getValidationState(storeName) {
        return {
            isValidating: this.activeValidations.has(storeName),
            validationResults: this.validationResults.get(storeName) || null,
            validationProgress: this.validationProgress.get(storeName) || {
                currentPage: 0,
                totalPages: 0,
                currentPageName: '',
                isActive: false
            },
            validationError: this.validationErrors.get(storeName) || null
        };
    }

    /**
     * Set validation results for a store (used when loading from database)
     * @param {string} storeName - Name of the store
     * @param {Object} results - Validation results
     */
    setValidationResults(storeName, results) {
        this.validationResults.set(storeName, results);
        this.notifyListeners();
    }

    /**
     * Clear validation error for a store
     * @param {string} storeName - Name of the store
     */
    clearValidationError(storeName) {
        this.validationErrors.delete(storeName);
        this.notifyListeners();
    }

    /**
     * Stop validation for a store
     * @param {string} storeName - Name of the store
     */
    stopValidation(storeName) {
        const validation = this.activeValidations.get(storeName);
        if (validation && validation.eventSource) {
            validation.eventSource.close();
            this.activeValidations.delete(storeName);
            this.validationProgress.delete(storeName);
            this.notifyListeners();
        }
    }

    /**
     * Add a listener for state changes
     * @param {Function} listener - Callback function
     * @returns {Function} - Unsubscribe function
     */
    addListener(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    /**
     * Notify all listeners of state change
     */
    notifyListeners() {
        this.listeners.forEach(listener => {
            try {
                listener();
            } catch (error) {
                console.error('Error in validation listener:', error);
            }
        });
    }

    /**
     * Get all active validations
     * @returns {Array} - Array of store names being validated
     */
    getActiveValidations() {
        return Array.from(this.activeValidations.keys());
    }
}

// Create and export a singleton instance
const globalValidationManager = new GlobalValidationManager();

export default globalValidationManager;
